import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, RefreshCw, Copy, Sparkles, ShieldCheck, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cmsCall } from "@/lib/cms";
import { Suggesties } from "@/pages/beheer/Suggesties";

const STAPPEN = [
  "Open claude.ai en ga naar Instellingen → Connectors (vereist Claude Pro, Max, Team of Enterprise).",
  "Kies \"Custom connector toevoegen\" en geef een naam, bijvoorbeeld \"NextProduct webshop\".",
  "Plak de geheime link hieronder als URL en kies \"Geen inlog\" (No sign-in). Sla op.",
  "Zet de connector aan in een gesprek en vraag Claude bijvoorbeeld: \"Lees eerst de briefing en het assortimentsoverzicht en adviseer welke producten we op het schap moeten zetten.\"",
];

const VOORBEELDVRAGEN = [
  "Welke producten hebben de laagste retailermarge en wat kunnen we aan de prijsstelling doen?",
  "Welke producten zijn niet op voorraad of missen foto's en teksten – wat pakken we eerst aan?",
  "Beoordeel de teksten op de homepage: overtuigen ze een drogist om NextBrush in te kopen?",
  "Hoe zou je de productdetailpagina anders indelen voor mobiel?",
  "Herschrijf de call-to-action op de homepage overtuigender en dien dat in als suggestie.",
];

export const Claude = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [briefing, setBriefing] = useState("");
  const basis = process.env.REACT_APP_BACKEND_URL;

  const laad = useCallback(() => {
    cmsCall("GET", "/beheer/claude", null, { admin: true })
      .then((d) => { setData(d); setBriefing(d.briefing || ""); })
      .catch((e) => toast.error(e.message));
  }, []);
  useEffect(laad, [laad]);

  const actie = async (fn, melding) => {
    setBusy(true);
    try {
      await fn();
      if (melding) toast.success(melding);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const nieuweLink = () => actie(async () => {
    const res = await cmsCall("POST", "/beheer/claude/link", null, { admin: true });
    setData((d) => ({ ...d, link: res.link }));
  }, "Nieuwe Claude-link aangemaakt. Een eventuele oude link werkt niet meer.");
  const intrekken = () => actie(async () => {
    await cmsCall("DELETE", "/beheer/claude/link", null, { admin: true });
    setData((d) => ({ ...d, link: null }));
  }, "Claude-link ingetrokken");
  const briefingOpslaan = () => actie(async () => {
    const res = await cmsCall("PUT", "/beheer/claude/briefing", { tekst: briefing }, { admin: true });
    setData((d) => ({ ...d, briefing: res.briefing, briefing_aangepast: res.briefing_aangepast }));
    setBriefing(res.briefing);
  }, "Briefing opgeslagen");
  const briefingReset = () => actie(async () => {
    const res = await cmsCall("PUT", "/beheer/claude/briefing", { tekst: "" }, { admin: true });
    setData((d) => ({ ...d, briefing: res.briefing, briefing_aangepast: false }));
    setBriefing(res.briefing);
  }, "Standaardbriefing hersteld");

  if (!data) return <Card className="p-6 text-sm text-muted-foreground">Laden…</Card>;
  const url = data.link ? `${basis}${data.link.pad}` : null;
  const briefingGewijzigd = briefing !== (data.briefing || "");

  return (
    <div className="space-y-6" data-testid="beheer-claude">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-lg font-semibold">Claude als meedenker</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Koppel je Claude-account aan deze webshop. Claude kan dan live meekijken in assortiment, prijzen en marges,
              voorraad, CMS-teksten, betalingen en systeemstatus en zo meedenken over problemen en strategie.
              Claude kan <strong>niets zelf wijzigen</strong>, maar wel verbeteringen voorstellen die jij hieronder doorvoert of afwijst.
            </p>
          </div>
        </div>
        <ol className="mt-5 space-y-2 text-sm">
          {STAPPEN.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary font-mono text-xs">{i + 1}</span>
              <span className="text-muted-foreground">{s}</span>
            </li>
          ))}
        </ol>
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-base font-semibold">Geheime link</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Behandel deze link als een wachtwoord: wie hem heeft, kan de data lezen. Maak een nieuwe aan als hij is uitgelekt.
        </p>
        {url ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border bg-secondary p-3">
              <div className="flex flex-wrap items-center gap-2">
                <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-xs" data-testid="claude-link-url">{url}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link gekopieerd"); }} data-testid="claude-link-copy">
                  <Copy className="mr-2 h-3.5 w-3.5" />Kopiëren
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="claude-link-meta">
              Aangemaakt door {data.link.aangemaakt_door} op {new Date(data.link.aangemaakt_op).toLocaleString("nl-NL")}
              {" · "}
              {data.link.laatst_gebruikt
                ? `laatst gebruikt ${new Date(data.link.laatst_gebruikt).toLocaleString("nl-NL")} (${data.link.aanroepen} aanroepen)`
                : "nog niet gebruikt door Claude"}
            </p>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={nieuweLink} disabled={busy} data-testid="claude-link-vernieuw">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Nieuwe link (oude vervalt)
              </Button>
              <Button variant="ghost" onClick={intrekken} disabled={busy} data-testid="claude-link-intrekken">
                <Trash2 className="mr-2 h-4 w-4" />Link intrekken
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <Alert><AlertDescription>Er is nog geen Claude-link. Maak er één aan en plak die in claude.ai als custom connector.</AlertDescription></Alert>
            <Button className="mt-4" onClick={nieuweLink} disabled={busy} data-testid="claude-link-maak">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Claude-link aanmaken
            </Button>
          </div>
        )}
      </Card>

      <Suggesties />

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-semibold">Briefing voor Claude</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Claude leest dit bij elk gesprek als eerste: wie NextProduct is, de merken, de doelgroep en jullie strategie.
              Hoe scherper de briefing, hoe beter het advies past.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={briefingReset} disabled={busy || !data.briefing_aangepast} data-testid="claude-briefing-reset">
            <RotateCcw className="mr-2 h-4 w-4" />Standaardtekst
          </Button>
        </div>
        <Label htmlFor="claude-briefing" className="mt-4 block text-xs text-muted-foreground">Briefing (max. 8000 tekens)</Label>
        <Textarea id="claude-briefing" value={briefing} onChange={(e) => setBriefing(e.target.value)} rows={10} maxLength={8000} className="mt-1 text-sm leading-relaxed" data-testid="claude-briefing-input" />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button onClick={briefingOpslaan} disabled={busy || !briefingGewijzigd} data-testid="claude-briefing-opslaan">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Briefing opslaan
          </Button>
          <span className="text-xs text-muted-foreground">{briefing.length}/8000</span>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="flex items-center gap-2 font-display text-base font-semibold"><ShieldCheck className="h-4 w-4 text-primary" />Wat Claude kan inzien</h3>
          <ul className="mt-3 space-y-2" data-testid="claude-tools">
            {(data.tools || []).map((t) => (
              <li key={t.name} className="rounded-md border px-3 py-2">
                <p className="font-mono text-xs font-semibold text-primary">{t.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t.description}</p>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-5">
          <h3 className="font-display text-base font-semibold">Vragen om mee te beginnen</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {VOORBEELDVRAGEN.map((v) => (
              <li key={v} className="flex gap-2 rounded-md bg-secondary px-3 py-2 text-muted-foreground">
                <span className="text-primary">“</span>{v}<span className="text-primary">”</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            Let op: omzet over álle retailers is nog niet beschikbaar via de CRM-koppeling; Claude ziet wel alle directe
            Mollie-betalingen en benoemt het zelf als data ontbreekt.
          </p>
        </Card>
      </div>
    </div>
  );
};
